/**
 * Platform Coupons Page — لوحة الإدارة العليا
 * ===============================================
 * Manage platform-level coupons (broader, strategic, cross-tenant).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../../context/ToastContext';
import SAR from '../../../components/SAR';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';

const api = async (path, opts = {}) => {
  const token = localStorage.getItem('watrly_admin_token');
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...opts,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'طلب فاشل');
  }
  return res.json();
};

// ── Icons ──────────────────────────────────────────────────────────────
const I = {
  plus:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  copy:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
  search:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  x:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  check:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  tag:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
  toggle:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="5" width="22" height="14" rx="7"/><circle cx="16" cy="12" r="3" fill="currentColor"/></svg>,
};

const COUPON_TYPES = [
  { value: 'general',       label: 'عام' },
  { value: 'first_order',   label: 'أول طلب' },
  { value: 'new_user',      label: 'مستخدم جديد' },
  { value: 'reengagement',  label: 'إعادة تفاعل' },
  { value: 'loyalty',       label: 'ولاء' },
  { value: 'campaign',      label: 'حملة' },
];

const STATUS_MAP = {
  active:    { label: 'نشط',    color: '#10b981', bg: '#ecfdf5' },
  expired:   { label: 'منتهي', color: '#ef4444', bg: '#fef2f2' },
  inactive:  { label: 'معطّل', color: '#6b7280', bg: '#f9fafb' },
  exhausted: { label: 'مستنفد',color: '#f59e0b', bg: '#fffbeb' },
};

const Chip = ({ status }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.inactive;
  return <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', color: s.color, background: s.bg }}>{s.label}</span>;
};

const TypeChip = ({ type }) => {
  const t = COUPON_TYPES.find(x => x.value === type) || { label: type };
  return <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '12px', background: '#e0e7ff', color: '#3730a3' }}>{t.label}</span>;
};

const StatCard = ({ label, value, color, onClick, active }) => (
  <button onClick={onClick} style={{
    flex: '1 1 140px', minWidth: '120px', padding: '16px 20px', borderRadius: '12px', cursor: 'pointer',
    background: active ? color + '18' : '#fff', border: `1.5px solid ${active ? color : '#e2e8f0'}`,
    textAlign: 'right', transition: 'all .18s',
  }}>
    <div style={{ fontSize: '26px', fontWeight: '700', color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{label}</div>
  </button>
);

// ── Main ───────────────────────────────────────────────────────────────
const PlatformCouponsPage = () => {
  const toast = useToast();

  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [coupons, setCoupons]     = useState([]);
  const [stats, setStats]         = useState({});
  const [tenants, setTenants]     = useState([]);
  const [filter, setFilter]       = useState('all');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch]       = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [codeStatus, setCodeStatus] = useState(null);
  const timer = useRef(null);

  const EMPTY = {
    code: '', name: '', name_ar: '', description: '',
    coupon_type: 'general', discount_type: 'percentage', discount_value: '',
    max_discount_amount: '', min_order_amount: '',
    valid_from: '', expires_at: '',
    max_redemptions: '', max_redemptions_per_customer: '1',
    applies_to_all_tenants: true, tenant_ids: [],
    first_order_only: false, new_customers_only: false,
  };
  const [form, setForm] = useState(EMPTY);

  // ── Load ─────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filter !== 'all') params.set('status', filter);
      if (typeFilter)       params.set('coupon_type', typeFilter);
      if (search)           params.set('search', search);

      const [cData, sData, tData] = await Promise.all([
        api(`/admin/platform-coupons?${params}`),
        api('/admin/platform-coupons/stats'),
        api('/tenants?limit=200'),
      ]);
      setCoupons(cData.items || []);
      setStats(sData || {});
      setTenants(tData.items || tData || []);
    } catch (e) { toast.error(e.message || 'فشل التحميل'); }
    finally     { setLoading(false); }
  }, [filter, typeFilter, search]);

  useEffect(() => { load(); }, [load]);

  // ── Code check ───────────────────────────────────────────────────
  const handleCode = (val) => {
    const code = val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5);
    setForm(f => ({ ...f, code }));
    setCodeStatus(null);
    if (timer.current) clearTimeout(timer.current);
    if (code.length === 5 && !editing) {
      setCodeStatus('checking');
      timer.current = setTimeout(async () => {
        try {
          const r = await api(`/admin/platform-coupons/check-code?code=${code}`);
          setCodeStatus(r.available ? 'ok' : 'taken');
        } catch { setCodeStatus(null); }
      }, 400);
    }
  };

  const genCode = async () => {
    try {
      const r = await api('/admin/platform-coupons/generate-code');
      setForm(f => ({ ...f, code: r.code }));
      setCodeStatus('ok');
    } catch { toast.error('فشل توليد الكود'); }
  };

  // ── Submit ───────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editing && codeStatus === 'taken')    { toast.error('الكود مستخدم');                    return; }
    if (form.code.length !== 5)                { toast.error('الكود يجب أن يكون 5 أحرف');         return; }
    if (!form.name)                            { toast.error('اسم الكوبون مطلوب');                return; }
    if (!form.applies_to_all_tenants && form.tenant_ids.length === 0) {
      toast.error('اختر شركة واحدة على الأقل'); return;
    }

    setSaving(true);
    try {
      const payload = {
        code: form.code, name: form.name, name_ar: form.name_ar, description: form.description,
        coupon_type: form.coupon_type,
        discount_type: form.discount_type, discount_value: parseFloat(form.discount_value) || 0,
        max_discount_amount: form.max_discount_amount ? parseFloat(form.max_discount_amount) : null,
        min_order_amount:    form.min_order_amount    ? parseFloat(form.min_order_amount)    : null,
        valid_from:  form.valid_from  ? new Date(form.valid_from).toISOString()  : null,
        expires_at:  form.expires_at  ? new Date(form.expires_at).toISOString()  : null,
        max_redemptions:     form.max_redemptions     ? parseInt(form.max_redemptions)     : null,
        max_redemptions_per_customer: parseInt(form.max_redemptions_per_customer) || 1,
        applies_to_all_tenants: form.applies_to_all_tenants,
        tenant_ids: form.tenant_ids,
        first_order_only: form.first_order_only,
        new_customers_only: form.new_customers_only,
      };

      if (editing) {
        await api(`/admin/platform-coupons/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('تم تحديث الكوبون');
      } else {
        await api('/admin/platform-coupons', { method: 'POST', body: JSON.stringify(payload) });
        toast.success('تم إنشاء الكوبون');
      }
      close(); load();
    } catch (err) { toast.error(err.message || 'فشلت العملية'); }
    finally { setSaving(false); }
  };

  // ── Toggle ───────────────────────────────────────────────────────
  const toggle = async (c) => {
    try {
      const r = await api(`/admin/platform-coupons/${c.id}/toggle`, { method: 'PATCH' });
      toast.success(r.message || 'تم'); load();
    } catch (e) { toast.error(e.message); }
  };

  // ── Delete ───────────────────────────────────────────────────────
  const del = async (c) => {
    if (!window.confirm(`إيقاف الكوبون "${c.code}"؟`)) return;
    try {
      await api(`/admin/platform-coupons/${c.id}`, { method: 'DELETE' });
      toast.success('تم الإيقاف'); load();
    } catch (e) { toast.error(e.message); }
  };

  const copy = (code) => navigator.clipboard.writeText(code).then(() => toast.success('تم النسخ'));

  const openEdit = async (c) => {
    setEditing(c); setCodeStatus(null);
    try {
      const d = await api(`/admin/platform-coupons/${c.id}`);
      setForm({
        code: d.code, name: d.name || '', name_ar: d.name_ar || '', description: d.description || '',
        coupon_type: d.coupon_type || 'general',
        discount_type: d.discount_type, discount_value: String(d.discount_value || ''),
        max_discount_amount: d.max_discount_amount ? String(d.max_discount_amount) : '',
        min_order_amount:    d.min_order_amount    ? String(d.min_order_amount)    : '',
        valid_from:  d.valid_from  ? d.valid_from.split('T')[0]  : '',
        expires_at:  d.expires_at  ? d.expires_at.split('T')[0]  : '',
        max_redemptions: d.max_redemptions ? String(d.max_redemptions) : '',
        max_redemptions_per_customer: String(d.max_redemptions_per_customer || 1),
        applies_to_all_tenants: d.applies_to_all_tenants !== false,
        tenant_ids: d.tenant_ids || [],
        first_order_only: d.first_order_only || false,
        new_customers_only: d.new_customers_only || false,
      });
    } catch { setForm({ ...EMPTY, ...c }); }
    setShowModal(true);
  };

  const close = () => { setShowModal(false); setEditing(null); setForm(EMPTY); setCodeStatus(null); };

  const toggleTenant = (tid) =>
    setForm(f => ({ ...f, tenant_ids: f.tenant_ids.includes(tid) ? f.tenant_ids.filter(x => x !== tid) : [...f.tenant_ids, tid] }));

  const fmtDate   = (d) => d ? new Date(d).toLocaleDateString('ar-SA') : '—';
  const fmtDiscount = (c) => c.discount_type === 'percentage'
    ? `${c.discount_value}%`
    : <>{c.discount_value} <SAR className="h-[0.75em] w-auto" /></>;

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div style={{ padding: '24px', direction: 'rtl', fontFamily: 'inherit', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '700' }}>كوبونات المنصة</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '14px' }}>كوبونات استراتيجية تطبّق على مستوى المنصة بأكملها</p>
        </div>
        <button onClick={() => { setEditing(null); setForm(EMPTY); setCodeStatus(null); setShowModal(true); }}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
          <span style={{ width: '18px', height: '18px' }}>{I.plus}</span> كوبون جديد
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
        <StatCard label="الكل"             value={stats.total || 0}      color="#6366f1" onClick={() => setFilter('all')}       active={filter === 'all'} />
        <StatCard label="نشط"              value={stats.active || 0}     color="#10b981" onClick={() => setFilter('active')}    active={filter === 'active'} />
        <StatCard label="منتهي"            value={stats.expired || 0}    color="#ef4444" onClick={() => setFilter('expired')}   active={filter === 'expired'} />
        <StatCard label="معطّل"            value={stats.inactive || 0}   color="#6b7280" onClick={() => setFilter('inactive')}  active={filter === 'inactive'} />
        <StatCard label="إجمالي الاستخدامات" value={stats.total_uses || 0} color="#3b82f6" onClick={() => {}} active={false} />
        <StatCard label="إجمالي الخصومات"  value={`${(stats.total_discount_given || 0).toFixed(0)}`} color="#8b5cf6" onClick={() => {}} active={false} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', width: '16px', height: '16px' }}>{I.search}</span>
          <input placeholder="ابحث بالكود أو الاسم…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding: '9px 34px 9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', minWidth: '240px' }} />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          style={{ padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#fff', cursor: 'pointer' }}>
          <option value="">كل الأنواع</option>
          {COUPON_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>جاري التحميل…</div>
      ) : coupons.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
          <div style={{ width: '48px', height: '48px', margin: '0 auto 12px', color: '#cbd5e1' }}>{I.tag}</div>
          <p style={{ fontWeight: '600' }}>لا توجد كوبونات</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {['الكود', 'الاسم', 'النوع', 'الخصم', 'الاستخدام', 'الانتهاء', 'النطاق', 'الحالة', ''].map((h, i) => (
                  <th key={i} style={{ padding: '12px 14px', fontWeight: '600', textAlign: 'right', color: '#475569', fontSize: '12px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {coupons.map((c, idx) => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: '700', fontSize: '15px', letterSpacing: '2px', color: '#2563eb' }}>{c.code}</span>
                      <button onClick={() => copy(c.code)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', width: '20px', height: '20px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{I.copy}</button>
                    </div>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ fontWeight: '600' }}>{c.name}</div>
                    {c.name_ar && <div style={{ fontSize: '12px', color: '#64748b' }}>{c.name_ar}</div>}
                  </td>
                  <td style={{ padding: '12px 14px' }}><TypeChip type={c.coupon_type} /></td>
                  <td style={{ padding: '12px 14px', fontWeight: '600' }}>
                    {fmtDiscount(c)}
                    {c.max_discount_amount && c.discount_type === 'percentage' &&
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>حد: {c.max_discount_amount} <SAR className="h-[0.65em] w-auto" /></div>}
                  </td>
                  <td style={{ padding: '12px 14px', fontVariantNumeric: 'tabular-nums' }}>
                    {c.redemption_count}
                    {c.max_redemptions && <span style={{ color: '#94a3b8' }}> / {c.max_redemptions}</span>}
                  </td>
                  <td style={{ padding: '12px 14px', fontSize: '13px', color: c.status_label === 'expired' ? '#ef4444' : '#475569' }}>{fmtDate(c.expires_at)}</td>
                  <td style={{ padding: '12px 14px' }}>
                    {c.applies_to_all_tenants
                      ? <span style={{ fontSize: '12px', color: '#059669', background: '#ecfdf5', padding: '2px 8px', borderRadius: '10px' }}>كل الشركات</span>
                      : <span style={{ fontSize: '12px', color: '#7c3aed', background: '#ede9fe', padding: '2px 8px', borderRadius: '10px' }}>شركات محددة</span>}
                  </td>
                  <td style={{ padding: '12px 14px' }}><Chip status={c.status_label} /></td>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button onClick={() => openEdit(c)} title="تعديل" style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '5px', cursor: 'pointer', color: '#475569', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{I.edit}</button>
                      <button onClick={() => toggle(c)} title={c.is_active ? 'تعطيل' : 'تفعيل'} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '5px', cursor: 'pointer', color: c.is_active ? '#6b7280' : '#10b981', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{I.toggle}</button>
                      <button onClick={() => del(c)} title="إيقاف" style={{ background: 'none', border: '1px solid #fecaca', borderRadius: '6px', padding: '5px', cursor: 'pointer', color: '#ef4444', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{I.trash}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal ────────────────────────────────────────────────────── */}
      {showModal && (
        <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '620px', maxHeight: '95vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}>
            {/* Modal header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>{editing ? 'تعديل كوبون' : 'كوبون منصة جديد'}</h2>
              <button onClick={close} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{I.x}</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

                {/* Code */}
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '13px' }}>كود الكوبون <span style={{ color: '#ef4444' }}>*</span></label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input maxLength={5} required value={form.code} onChange={e => handleCode(e.target.value)} disabled={!!editing}
                        placeholder="5 أحرف / أرقام"
                        style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'monospace', fontWeight: '700', fontSize: '20px', letterSpacing: '5px', textAlign: 'center', textTransform: 'uppercase', padding: '12px', border: `1.5px solid ${codeStatus === 'ok' ? '#10b981' : codeStatus === 'taken' ? '#ef4444' : '#e2e8f0'}`, borderRadius: '10px', outline: 'none' }} />
                      {codeStatus === 'checking' && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: '#94a3b8' }}>جاري التحقق…</span>}
                      {codeStatus === 'ok'       && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#10b981', width: '18px', height: '18px' }}>{I.check}</span>}
                      {codeStatus === 'taken'    && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#ef4444', fontSize: '11px' }}>مستخدم</span>}
                    </div>
                    {!editing && (
                      <button type="button" onClick={genCode} style={{ padding: '12px 16px', border: '1.5px solid #e2e8f0', borderRadius: '10px', background: '#f8fafc', cursor: 'pointer', display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px', whiteSpace: 'nowrap' }}>
                        <span style={{ width: '15px', height: '15px' }}>{I.refresh}</span> توليد
                      </button>
                    )}
                  </div>
                  {codeStatus === 'taken' && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>الكود مستخدم. اختر كوداً آخر.</p>}
                </div>

                {/* Name */}
                <div>
                  <label style={lbl}>اسم الكوبون <span style={{ color: '#ef4444' }}>*</span></label>
                  <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ramadan 2025" style={inp} />
                </div>
                <div>
                  <label style={lbl}>الاسم بالعربي</label>
                  <input value={form.name_ar} onChange={e => setForm(f => ({ ...f, name_ar: e.target.value }))} placeholder="رمضان 2025" style={inp} />
                </div>

                {/* Description */}
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={lbl}>الوصف (اختياري)</label>
                  <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="وصف مختصر للكوبون" style={inp} />
                </div>

                {/* Coupon type */}
                <div>
                  <label style={lbl}>نوع الكوبون</label>
                  <select value={form.coupon_type} onChange={e => setForm(f => ({ ...f, coupon_type: e.target.value }))} style={sel}>
                    {COUPON_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                {/* Discount type */}
                <div>
                  <label style={lbl}>نوع الخصم</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {[{ v: 'percentage', l: <span>نسبة مئوية (%)</span> }, { v: 'fixed_amount', l: <span>مبلغ ثابت (<SAR style={{ width: '0.85em', height: '0.85em' }} />)</span> }].map(opt => (
                      <button key={opt.v} type="button"
                        onClick={() => setForm(f => ({ ...f, discount_type: opt.v, max_discount_amount: '' }))}
                        style={{ flex: 1, padding: '9px 12px', borderRadius: '8px', border: `1.5px solid ${form.discount_type === opt.v ? '#2563eb' : '#e2e8f0'}`, background: form.discount_type === opt.v ? '#eff6ff' : '#fff', color: form.discount_type === opt.v ? '#2563eb' : 'inherit', fontWeight: form.discount_type === opt.v ? '600' : '400', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Discount value */}
                <div>
                  <label style={lbl}>قيمة الخصم <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="number" required min="0.01" max={form.discount_type === 'percentage' ? 100 : undefined} step="0.01"
                    value={form.discount_value} onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))}
                    placeholder={form.discount_type === 'percentage' ? 'مثال: 20' : 'مثال: 50'} style={inp} />
                </div>

                {/* Max discount cap */}
                {form.discount_type === 'percentage' && (
                  <div>
                    <label style={lbl}>الحد الأقصى للخصم</label>
                    <input type="number" min="0" step="0.01" value={form.max_discount_amount}
                      onChange={e => setForm(f => ({ ...f, max_discount_amount: e.target.value }))}
                      placeholder="مثال: 100" style={inp} />
                  </div>
                )}

                {/* Min order */}
                <div>
                  <label style={lbl}>الحد الأدنى للطلب</label>
                  <input type="number" min="0" step="0.01" value={form.min_order_amount}
                    onChange={e => setForm(f => ({ ...f, min_order_amount: e.target.value }))}
                    placeholder="مثال: 100" style={inp} />
                </div>

                {/* Date range */}
                <div>
                  <label style={lbl}>تاريخ البدء</label>
                  <input type="date" value={form.valid_from} onChange={e => setForm(f => ({ ...f, valid_from: e.target.value }))} style={inp} />
                </div>
                <div>
                  <label style={lbl}>تاريخ الانتهاء</label>
                  <input type="date" value={form.expires_at} min={form.valid_from || new Date().toISOString().split('T')[0]}
                    onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} style={inp} />
                </div>

                {/* Redemption limits */}
                <div>
                  <label style={lbl}>الحد الأقصى الكلي للاستخدام</label>
                  <input type="number" min="1" value={form.max_redemptions}
                    onChange={e => setForm(f => ({ ...f, max_redemptions: e.target.value }))}
                    placeholder="فارغ = غير محدود" style={inp} />
                </div>
                <div>
                  <label style={lbl}>الحد لكل عميل</label>
                  <select value={form.max_redemptions_per_customer}
                    onChange={e => setForm(f => ({ ...f, max_redemptions_per_customer: e.target.value }))} style={sel}>
                    <option value="1">مرة واحدة</option>
                    <option value="2">مرتين</option>
                    <option value="3">3 مرات</option>
                    <option value="5">5 مرات</option>
                    <option value="0">غير محدود</option>
                  </select>
                </div>

                {/* Rules */}
                <div style={{ gridColumn: '1/-1', display: 'flex', gap: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                    <input type="checkbox" checked={form.first_order_only} onChange={e => setForm(f => ({ ...f, first_order_only: e.target.checked }))} style={{ accentColor: '#2563eb', width: '16px', height: '16px' }} />
                    أول طلب فقط
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                    <input type="checkbox" checked={form.new_customers_only} onChange={e => setForm(f => ({ ...f, new_customers_only: e.target.checked }))} style={{ accentColor: '#2563eb', width: '16px', height: '16px' }} />
                    العملاء الجدد فقط
                  </label>
                </div>

                {/* Tenant scope */}
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={lbl}>نطاق تطبيق الشركات</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    {[{ v: true, l: 'كل الشركات' }, { v: false, l: 'شركات محددة' }].map(opt => (
                      <label key={String(opt.v)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '10px 16px', border: `1.5px solid ${form.applies_to_all_tenants === opt.v ? '#2563eb' : '#e2e8f0'}`, borderRadius: '10px', background: form.applies_to_all_tenants === opt.v ? '#eff6ff' : 'transparent', fontSize: '13px' }}>
                        <input type="radio" name="scope" checked={form.applies_to_all_tenants === opt.v}
                          onChange={() => setForm(f => ({ ...f, applies_to_all_tenants: opt.v, tenant_ids: [] }))}
                          style={{ accentColor: '#2563eb' }} />
                        {opt.l}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Tenant multi-select */}
                {!form.applies_to_all_tenants && (
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={lbl}>اختر الشركات <span style={{ color: '#ef4444' }}>*</span></label>
                    {tenants.length === 0 ? (
                      <p style={{ color: '#94a3b8', fontSize: '13px' }}>لا توجد شركات</p>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: '180px', overflowY: 'auto', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
                        {tenants.map(t => {
                          const sel = form.tenant_ids.includes(t.id);
                          return (
                            <button key={t.id} type="button" onClick={() => toggleTenant(t.id)}
                              style={{ padding: '6px 12px', borderRadius: '20px', border: `1.5px solid ${sel ? '#2563eb' : '#e2e8f0'}`, background: sel ? '#eff6ff' : 'transparent', cursor: 'pointer', fontSize: '13px', color: sel ? '#2563eb' : 'inherit', display: 'flex', alignItems: 'center', gap: '5px' }}>
                              {sel && <span style={{ width: '14px', height: '14px' }}>{I.check}</span>}
                              {t.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {form.tenant_ids.length > 0 && <p style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>تم اختيار {form.tenant_ids.length} شركة</p>}
                  </div>
                )}

              </div>

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', borderTop: '1px solid #e2e8f0' }}>
                <button type="button" onClick={close} style={{ padding: '10px 20px', background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>إلغاء</button>
                <button type="submit" disabled={saving || codeStatus === 'taken'}
                  style={{ padding: '10px 24px', background: saving ? '#93c5fd' : '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px' }}>
                  {saving ? 'جاري الحفظ…' : editing ? 'حفظ التعديلات' : 'إنشاء الكوبون'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// shared tiny style helpers
const lbl = { display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '13px', color: '#374151' };
const inp = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#fff' };
const sel = { ...inp, cursor: 'pointer', appearance: 'none', paddingLeft: '28px' };

export default PlatformCouponsPage;
